import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  input,
  output,
  viewChild,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OutlineParseError } from '../../utils/outline-adapter';
import { FlashMapOutlineTranslations, FLASH_MAP_TRANSLATIONS } from '../../i18n/flash-map-i18n';

@Component({
  selector: 'lib-outline-editor',
  templateUrl: './outline-editor.component.html',
  styleUrls: ['./outline-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class OutlineEditorComponent {
  readonly text = input<string>('');
  readonly selectedLineIndex = input<number | null>(null);
  readonly error = input<OutlineParseError | null>(null);
  readonly translations = input<FlashMapOutlineTranslations>(FLASH_MAP_TRANSLATIONS.en.outline);
  readonly disabled = input<boolean>(false);

  readonly textChange = output<string>();
  readonly lineSelect = output<number>();

  readonly editorRef = viewChild<ElementRef<HTMLTextAreaElement>>('editor');

  private lastSelectedLine: number | null = null;
  private isUserEditing = signal(false);
  private externalTextVersion = signal(0);

  readonly displayText = computed(() => {
    this.externalTextVersion();
    if (this.isUserEditing()) {
      return this.editorRef()?.nativeElement?.value ?? this.text();
    }
    return this.text();
  });

  constructor() {
    effect(() => {
      const inputText = this.text();
      if (!this.isUserEditing()) {
        this.externalTextVersion.update(v => v + 1);
      }
    });

    effect(() => {
      const lineIndex = this.selectedLineIndex();
      const editor = this.editorRef();
      if (!editor || lineIndex === null || lineIndex === this.lastSelectedLine) {
        return;
      }
      const textarea = editor.nativeElement;
      const lines = textarea.value.split('\n');
      if (lineIndex < 0 || lineIndex >= lines.length) {
        return;
      }
      let start = 0;
      for (let i = 0; i < lineIndex; i += 1) {
        start += lines[i].length + 1;
      }
      const end = start + lines[lineIndex].length;
      textarea.setSelectionRange(start, end);
      textarea.focus();
      this.lastSelectedLine = lineIndex;
    });
  }

  onInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.isUserEditing.set(true);
    this.textChange.emit(textarea.value);
    this.emitSelection(textarea);
  }

  onSelectionChange(): void {
    const textarea = this.editorRef()?.nativeElement;
    if (!textarea) {
      return;
    }
    this.emitSelection(textarea);
  }

  onFocus(): void {
    this.isUserEditing.set(true);
  }

  onBlur(): void {
    this.isUserEditing.set(false);
  }

  onKeydown(event: KeyboardEvent): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (this.disabled()) {
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      const isShift = event.shiftKey;
      this.applyIndent(textarea, !isShift);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.insertSiblingLine(textarea);
      return;
    }

    if (event.key === 'Backspace') {
      const handled = this.removeEmptyLine(textarea);
      if (handled) {
        event.preventDefault();
      }
    }
  }

  private applyIndent(textarea: HTMLTextAreaElement, indent: boolean): void {
    const { lineStart, lineEnd, lineText } = this.getCurrentLine(textarea);
    let updatedLine = lineText;
    let cursorOffset = 0;

    if (indent) {
      updatedLine = `\t${lineText}`;
      cursorOffset = 1;
    } else {
      if (lineText.startsWith('\t')) {
        updatedLine = lineText.substring(1);
        cursorOffset = -1;
      } else if (lineText.startsWith('  ')) {
        updatedLine = lineText.substring(2);
        cursorOffset = -2;
      }
    }

    const newValue = textarea.value.slice(0, lineStart) + updatedLine + textarea.value.slice(lineEnd);
    const newCursor = Math.max(lineStart, textarea.selectionStart + cursorOffset);
    this.applyTextUpdate(textarea, newValue, newCursor, newCursor);
  }

  private insertSiblingLine(textarea: HTMLTextAreaElement): void {
    const { lineStart, lineEnd, lineText } = this.getCurrentLine(textarea);
    const indentMatch = lineText.match(/^[\t ]*/);
    const indent = indentMatch ? indentMatch[0] : '';
    const insertText = `\n${indent}`;
    const newValue = textarea.value.slice(0, lineEnd) + insertText + textarea.value.slice(lineEnd);
    const newCursor = lineEnd + insertText.length;
    this.applyTextUpdate(textarea, newValue, newCursor, newCursor);
  }

  private removeEmptyLine(textarea: HTMLTextAreaElement): boolean {
    const { lineStart, lineEnd, lineText, lineIndex } = this.getCurrentLine(textarea);
    if (lineText.trim().length > 0) {
      return false;
    }
    const before = textarea.value.slice(0, lineStart);
    const after = textarea.value.slice(lineEnd);
    let newValue = before + after;
    if (before.endsWith('\n')) {
      newValue = before.slice(0, -1) + after;
    } else if (after.startsWith('\n')) {
      newValue = before + after.slice(1);
    }
    const cursor = Math.max(0, lineStart - 1);
    this.applyTextUpdate(textarea, newValue, cursor, cursor);
    this.lastSelectedLine = Math.max(0, lineIndex - 1);
    return true;
  }

  private getCurrentLine(textarea: HTMLTextAreaElement): {
    lineStart: number;
    lineEnd: number;
    lineText: string;
    lineIndex: number;
  } {
    const value = textarea.value;
    const cursor = textarea.selectionStart ?? 0;
    const before = value.slice(0, cursor);
    const lineIndex = before.split('\n').length - 1;
    const lineStart = value.lastIndexOf('\n', cursor - 1) + 1;
    const nextNewline = value.indexOf('\n', cursor);
    const lineEnd = nextNewline === -1 ? value.length : nextNewline;
    return {
      lineStart,
      lineEnd,
      lineText: value.slice(lineStart, lineEnd),
      lineIndex,
    };
  }

  private applyTextUpdate(
    textarea: HTMLTextAreaElement,
    value: string,
    selectionStart: number,
    selectionEnd: number
  ): void {
    textarea.value = value;
    textarea.setSelectionRange(selectionStart, selectionEnd);
    this.textChange.emit(value);
    this.emitSelection(textarea);
  }

  private emitSelection(textarea: HTMLTextAreaElement): void {
    const cursor = textarea.selectionStart ?? 0;
    const lineIndex = textarea.value.slice(0, cursor).split('\n').length - 1;
    this.lastSelectedLine = lineIndex;
    this.lineSelect.emit(lineIndex);
  }

  getErrorMessage(error: OutlineParseError | null): string {
    if (!error) {
      return '';
    }
    return this.translations().errors[error.code];
  }
}

